/**
 * Chart configuration presets.
 * These provide pre-tuned "feels" for different use cases.
 */

import type { ChartConfig, DeepPartial } from './types';

/**
 * Available chart presets.
 * Each preset provides a distinct visual and behavioral feel.
 */
export const CHART_PRESETS = {
    /**
     * Default preset - balanced response.
     * Good all-around feel with moderate smoothness and responsiveness.
     */
    default: {},

    /**
     * Snappy preset - fast, responsive feel.
     * Best for users who want immediate visual feedback.
     * The cursor quickly tracks price changes with minimal lag.
     */
    snappy: {
        physics: {
            tension: 35,
            friction: 8,
            adaptiveTension: {
                threshold: 1.0,
                exponent: 2.0,
            },
        },
        scaling: {
            lerpFactor: 0.15,
        },
    },

    /**
     * Smooth preset - fluid, graceful movement.
     * Best for a calmer, more elegant visualization.
     * The cursor glides smoothly between price points.
     */
    smooth: {
        physics: {
            tension: 12,
            friction: 5,
            adaptiveTension: {
                threshold: 3.0,
                exponent: 1.2,
            },
        },
        scaling: {
            lerpFactor: 0.01,
        },
    },

    /**
     * Medical monitor preset - clinical, precise feel.
     * Mimics the aesthetic of medical monitoring equipment.
     * Very responsive with tight camera tracking.
     */
    medicalMonitor: {
        physics: {
            tension: 50,
            friction: 10,
            enableArtificialNoise: false,
        },
        scaling: {
            lerpFactor: 0.2,
            padding: 1.1,
        },
    },

    /**
     * Organic preset - natural, breathing movement.
     * Includes subtle drift and micro-movements for a "living" feel.
     * Best for artistic or experimental visualizations.
     */
    organic: {
        physics: {
            tension: 15,
            friction: 4,
            // fractions of the asset's visible spread (see calculatePhysicsStep);
            // livelier than the default 2.5 / 0.4 for a more "breathing" feel.
            enableArtificialNoise: true,
            noiseStrength: 4,
            organicDriftScale: 0.8,
        },
    },

    /**
     * Aggressive preset - high volatility emphasis.
     * Exaggerates price movements for dramatic effect.
     * Good for demo/presentation purposes.
     */
    aggressive: {
        physics: {
            tension: 40,
            friction: 5,
            adaptiveTension: {
                threshold: 0.8,
                exponent: 2.5,
                multiplier: 1.2,
            },
        },
        scaling: {
            lerpFactor: 0.12,
            padding: 1.5,
        },
    },
} satisfies Record<string, DeepPartial<ChartConfig>>;

/**
 * Type representing available preset names.
 */
export type ChartPreset = keyof typeof CHART_PRESETS;

/**
 * Array of all available preset names for UI selection.
 */
export const PRESET_NAMES = Object.keys(CHART_PRESETS) as ChartPreset[];
